import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Grid, Alert, CircularProgress } from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTemplateVersions } from '../TemplateEditor/hooks/useTemplateVersions';
import { useContentCheckAssignment } from '../ContentCheckPolicyManager/hooks/useContentCheckAssignment';
import { TemplateVersionSelect } from '../common';

// Import custom hooks
import {
  useTemplateData,
  useExecutionData,
  useExecutionForm,
  useTemplateStats,
  useUserFeedback,
} from './hooks/useExecutionTester';
import { usePreflightExecution } from './hooks/useExecutionTesterHooks/usePreflightExecution';

// Import components
import ExecutionTesterHeader from './components/ExecutionTesterHeader';
import ExecutionForm from './components/ExecutionForm';
import ResultsPanel from './components/ResultsPanel';
import TemplateInformation from './components/TemplateInformation';
import TemplateStatistics from './components/TemplateStatistics';
import UserFeedback from './components/UserFeedback';
import ExecutionHistoryDialog from './components/ExecutionHistoryDialog';
import FeedbackDialog from './components/FeedbackDialog';

const ExecutionTester = () => {
  const { templateId } = useParams();
  const { tenant } = useAuth();
  const navigate = useNavigate();

  // State for UI interactions
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);

  // Load template versions
  const { versions } = useTemplateVersions(templateId);

  // Load security policies for the template
  const { assignments } = useContentCheckAssignment(templateId);

  // Custom hooks for data management
  const {
    template,
    loading: templateLoading,
    error: templateError,
    isStale: templateIsStale,
  } = useTemplateData(templateId, tenant, selectedVersion?.version_number);

  const {
    executions,
    loading: executionsLoading,
    executing,
    error: executionError,
    success,
    executeTemplate,
    executeTemplateSimplified,
    setError,
    setSuccess,
    loadExecutions,
  } = useExecutionData(
    templateId,
    tenant,
    template?.name || template?.template_name
  );

  // Preflight-first execution hook
  // Note: We combine executing states from both hooks for UI
  const {
    executeWithPreflight,
    preflightStatus,
    estimatedTime,
    executionMode,
    executionStatus,
    executionProgress,
    isExecuting: isPreflightExecuting, // Get executing state from preflight hook
    resetPreflight,
    PREFLIGHT_SYNC_THRESHOLD,
  } = usePreflightExecution(
    templateId,
    template?.name || template?.template_name
  );

  // Combined executing state: true if either hook is executing
  const isExecuting = executing || isPreflightExecuting;

  const {
    executionData,
    setExecutionData,
    showSettings,
    setShowSettings,
    parseTemplateVariables,
    handleInputDataChange,
    addInputField,
    removeInputField,
    handleExecutionSettingChange,
    resetForm,
  } = useExecutionForm(template);

  const { templateStats } = useTemplateStats(executions, templateId);

  const {
    userFeedbacks,
    feedbackDialogOpen,
    setFeedbackDialogOpen,
    newFeedback,
    setNewFeedback,
    loadUserFeedbacks,
    submitFeedback,
  } = useUserFeedback(templateId);

  // Load user feedbacks on component mount
  useEffect(() => {
    loadUserFeedbacks();
  }, [loadUserFeedbacks]);

  // Set default version when versions load
  useEffect(() => {
    if (versions.length > 0 && !selectedVersion) {
      // Default to the first version if available
      setSelectedVersion(versions[0]);
    }
  }, [versions, selectedVersion]);

  // Track previous version to avoid unnecessary refreshes
  const prevVersionRef = useRef(selectedVersion?.version_number);

  // Only reload template if version actually changed
  useEffect(() => {
    const currentVersion = selectedVersion?.version_number;
    if (
      currentVersion !== undefined &&
      currentVersion !== prevVersionRef.current
    ) {
      prevVersionRef.current = currentVersion;
      // Template will reload automatically via useTemplateData hook
    }
  }, [selectedVersion?.version_number]);

  // Handle execution with preflight-first logic
  const handleExecuteTemplate = async payload => {
    try {
      // Clear previous results to prepare for new execution
      setSelectedExecution(null);
      resetPreflight();

      // Use preflight-first execution
      const result = await executeWithPreflight(payload, preflightInfo => {
        // Optional: Show preflight info to user
        console.log('Preflight complete:', {
          estimated_time: preflightInfo.estimated_time,
          execution_id: preflightInfo.execution_id,
        });
      });

      // Set success message based on execution mode
      if (executionMode === 'sync') {
        setSuccess(
          `Template executed successfully! (Sync mode, estimated ${estimatedTime?.toFixed(1)}s)`
        );
      } else {
        setSuccess(
          `Template executed successfully! (Async mode, estimated ${estimatedTime?.toFixed(1)}s)`
        );
      }

      // Reload executions to show new result
      await loadExecutions();

      // Select the new execution
      if (result?.id) {
        setSelectedExecution(result.id);
      }

      return result;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const handleExecuteTemplateSimplified = async payload => {
    try {
      // Clear previous results to prepare for new execution
      setSelectedExecution(null);
      const result = await executeTemplateSimplified(payload);
      setSelectedExecution(result.id);
      return result;
    } catch (error) {
      throw error;
    }
  };

  // Handle feedback submission with success message
  const handleSubmitFeedback = async () => {
    try {
      const message = await submitFeedback();
      setSuccess(message);
    } catch (error) {
      setError(error.message);
    }
  };

  // Handle dialog close with error clearing
  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
  };

  const handleCloseFeedbackDialog = () => {
    setFeedbackDialogOpen(false);
  };

  // Handle edit template navigation
  const handleEditTemplate = () => {
    if (templateId) {
      navigate(`/templates/${templateId}/edit`);
    }
  };

  // Show loading state only on initial load (not when switching versions)
  // When templateIsStale is true, we're showing previous template while loading new version
  const isInitialLoad = templateLoading && !template && !templateIsStale;
  if (isInitialLoad || executionsLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Show subtle loading indicator when switching versions */}
      {templateIsStale && (
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{
            mb: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            },
          }}
        >
          Loading version {selectedVersion?.version_number}...
        </Alert>
      )}

      {/* Header */}
      <ExecutionTesterHeader template={template} />

      {/* Version Selection */}
      {versions.length > 0 && (
        <Box sx={{ mb: 3, minWidth: 200 }}>
          <TemplateVersionSelect
            versions={versions}
            value={selectedVersion?.version_number || ''}
            onChange={(versionValue, versionObj) => {
              setSelectedVersion(versionObj);
            }}
            label="Template Version"
            size="small"
            fullWidth={false}
            displayEmpty={false}
            showTooltip={true}
            securityPolicies={assignments}
            // Advanced API returns: { version_number: number, status: string, created_at: string, llm_config: object }
            getVersionValue={version => version.version_number}
            getVersionNumber={version => version.version_number || ''}
            getVersionStatus={version => version.status || null}
            getVersionDate={version => version.created_at || null}
          />
        </Box>
      )}

      {/* Preflight Status */}
      {preflightStatus === 'validating' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Validating preflight checks... (estimating execution time)
        </Alert>
      )}

      {preflightStatus === 'complete' && estimatedTime !== null && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Preflight complete: Estimated execution time{' '}
          {estimatedTime.toFixed(1)}s
          {estimatedTime < PREFLIGHT_SYNC_THRESHOLD
            ? ` (using sync execution)`
            : ` (using async execution with polling)`}
        </Alert>
      )}

      {/* Success/Error Messages */}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {(templateError || executionError) && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          onClose={() => {
            setError(null);
          }}
        >
          {templateError || executionError}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Execution Form */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ExecutionForm
            template={template}
            executionData={executionData}
            setExecutionData={setExecutionData}
            showSettings={showSettings}
            setShowSettings={setShowSettings}
            executing={isExecuting}
            templateId={templateId}
            onExecuteTemplate={handleExecuteTemplate}
            onExecuteTemplateSimplified={handleExecuteTemplateSimplified}
            onResetForm={resetForm}
            onEditTemplate={handleEditTemplate}
            parseTemplateVariables={parseTemplateVariables}
            handleInputDataChange={handleInputDataChange}
            addInputField={addInputField}
            removeInputField={removeInputField}
            handleExecutionSettingChange={handleExecutionSettingChange}
            selectedVersion={selectedVersion}
          />
        </Grid>

        {/* Results Panel */}
        <Grid size={{ xs: 12, md: 6 }}>
          <ResultsPanel
            executions={executions}
            selectedExecution={selectedExecution}
            setSelectedExecution={setSelectedExecution}
            onLoadExecutions={() => {}} // This will be handled by the hook automatically
            onOpenHistoryDialog={() => setHistoryDialogOpen(true)}
            executing={isExecuting}
            executionStatus={executionStatus}
            executionProgress={executionProgress}
          />
        </Grid>
      </Grid>

      {/* Template Summary Panel - One Row */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Template Information */}
        <Grid size={{ xs: 12, md: 4 }}>
          <TemplateInformation
            template={template}
            templateId={templateId}
            parseTemplateVariables={parseTemplateVariables}
          />
        </Grid>

        {/* Template Statistics */}
        <Grid size={{ xs: 12, md: 4 }}>
          <TemplateStatistics
            templateStats={templateStats}
            onOpenFeedbackDialog={() => setFeedbackDialogOpen(true)}
          />
        </Grid>

        {/* User Feedback */}
        <Grid size={{ xs: 12, md: 4 }}>
          <UserFeedback userFeedbacks={userFeedbacks} />
        </Grid>
      </Grid>

      {/* Execution History Dialog */}
      <ExecutionHistoryDialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        executions={executions}
        onSelectExecution={setSelectedExecution}
        templateId={templateId}
      />

      {/* Feedback Dialog */}
      <FeedbackDialog
        open={feedbackDialogOpen}
        onClose={handleCloseFeedbackDialog}
        newFeedback={newFeedback}
        setNewFeedback={setNewFeedback}
        onSubmitFeedback={handleSubmitFeedback}
      />
    </Container>
  );
};

export default ExecutionTester;
